import {
  extractedErrorObject,
  playlistApi,
  SharePlaylist,
  UnsharePlaylistRequestBody,
} from '@api/swagger'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import useLoadingContext from '@hooks/useLoadingContext'
import { useLoadingIds } from '@hooks/useLoadingIds'
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PersonIcon from '@mui/icons-material/Person'
import DeleteIcon from '@mui/icons-material/Delete'
import PlaylistEmptyState from './PlaylistEmptyState'
import ActionButtons from '@components/Buttons/ActionButtons'
import { ContainedButtonVariant } from '@components/Buttons/ContainedButton'
import { TextButtonVariant } from '@components/Buttons/TextButton'
import ConfirmationModal from '@components/Modals/ConfirmationModal'

export interface SharedUsersListProps {
  playlistKey: number
}

const notFoundCode = 'Not Found'

const SharedUsersList: React.FC<SharedUsersListProps> = ({ playlistKey }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const {
    Playlist: {
      fetchSharePlaylist: fetchSharePlaylistLoadingId,
      unsharePlaylist: unsharePlaylistLoadingId,
    },
  } = useLoadingIds()
  const { handleError } = useAppSnackbar()
  const [sharedPlaylistUsers, setSharedPlaylistUsers] = useState<
    Array<SharePlaylist>
  >([])

  const [selectedUserToUnshare, setSelectedUserToUnshare] =
    useState<UnsharePlaylistRequestBody>({ email: '' })
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const sharedPlaylistUsersInitialValue = useRef<Array<SharePlaylist>>([])
  const fetchSharePlaylist = async () => {
    try {
      const sharePlaylist = await playlistApi.fetchSharePlaylist({
        playlistKey,
      })
      setSharedPlaylistUsers(sharePlaylist)

      sharedPlaylistUsersInitialValue.current = sharePlaylist
    } catch (error) {
      const result = await extractedErrorObject(error)
      if (result && result.code !== notFoundCode) {
        await handleError(
          error,
          t(
            'SharedUsersList.FetchSharePlaylist.Error',
            'An unknown error occurred fetching playlist share.'
          )
        )
      }
    }
  }

  const { triggerFetch, isLoading: isFetchSharePlaylistLoading } =
    useLoadingContext({
      asyncFunction: fetchSharePlaylist,
      loadingId: fetchSharePlaylistLoadingId,
    })

  const deleteSharedUser = async () => {
    try {
      await playlistApi.unsharePlaylist({
        body: selectedUserToUnshare,
        playlistKey,
      })
      setShowConfirmationModal(false)
      setSharedPlaylistUsers((prev) => {
        const newValue = prev.filter(
          (user) => user.email !== selectedUserToUnshare.email
        )
        sharedPlaylistUsersInitialValue.current = newValue
        return newValue
      })
    } catch (error) {
      await handleError(
        error,
        t(
          'SharedUsersList.UnsharePlaylist.Error',
          'An unknown error occurred removing shared user.'
        )
      )
    }
  }

  const {
    triggerFetch: deleteUserSharePlaylist,
    isLoading: deleteUserSharePlaylistIsLoading,
  } = useLoadingContext({
    asyncFunction: deleteSharedUser,
    loadingId: unsharePlaylistLoadingId,
  })

  const handleCancel = () => {
    setShowConfirmationModal(false)
  }
  const handleUnSharePlaylist = (record: UnsharePlaylistRequestBody) => {
    setShowConfirmationModal(true)
    setSelectedUserToUnshare(record)
  }

  const confirmationButtons = (
    <ActionButtons
      primaryButtonLabel={ContainedButtonVariant.YesRemove}
      secondaryButtonLabel={TextButtonVariant.NoCancel}
      secondaryClick={handleCancel}
      primaryButtonLoadingId={unsharePlaylistLoadingId}
      disablePrimaryButton={deleteUserSharePlaylistIsLoading}
    />
  )

  const headerMessage = `${t(
    'Playlist.Sharing.Confirmation.Header',
    'You are about to remove "{{email}}" from this playlist.',
    { email: selectedUserToUnshare.email }
  )}`

  const confirmationBodyMessage = (
    <Typography variant="body1" component="p" align="center">
      {t(
        'Playlist.Sharing.Confirmation.Body',
        'This action is not reversible and by confirming "Yes", this user will lose access to the playlist.'
      )}
    </Typography>
  )

  const roleRemovalConfirmationProps = {
    isOpen: showConfirmationModal,
    dialogTitle: headerMessage,
    dialogContent: confirmationBodyMessage,
    dialogActions: confirmationButtons,
    handleFormSubmit: deleteUserSharePlaylist,
  }

  useEffect(() => {
    triggerFetch()
  }, [triggerFetch])

  const hasCurrentUsers = sharedPlaylistUsers.length > 0
  const hasInitialUsers = sharedPlaylistUsersInitialValue.current.length > 0
  const shouldShowEmptyState = !sharedPlaylistUsersInitialValue.current.length
  const shouldDisplayUsers =
    hasCurrentUsers || (hasInitialUsers && !isFetchSharePlaylistLoading)

  return (
    <Box
      sx={
        hasCurrentUsers
          ? {
              height: '49vh',
              maxHeight: '49vh',
              overflowY: 'auto',
              paddingX: theme.spacing(2),
              paddingBottom: theme.spacing(0.3),
            }
          : undefined
      }
    >
      {shouldDisplayUsers
        ? (sharedPlaylistUsers.length
            ? sharedPlaylistUsers
            : sharedPlaylistUsersInitialValue.current
          ).map((inviteInfo, index) => {
            return (
              <Card
                sx={{
                  marginTop: index > 0 ? theme.spacing(1) : undefined,
                }}
                key={`sharedPlaylistInfo-${index}`}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: theme.spacing(1.2),
                    justifyContent: 'space-between',
                  }}
                >
                  <Box display={'flex'} sx={{ minWidth: 0 }}>
                    <Box pr={theme.spacing(2)}>
                      <PersonIcon />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      component="p"
                      sx={{
                        [theme.breakpoints.down(500)]: {
                          flex: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    >
                      {inviteInfo.email}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      onClick={() =>
                        handleUnSharePlaylist({
                          email: inviteInfo.email,
                          userKey: inviteInfo?.userKey
                            ? `${inviteInfo?.userKey}`
                            : undefined,
                        })
                      }
                    >
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            )
          })
        : shouldShowEmptyState && (
            <Box width={'100%'}>
              <PlaylistEmptyState
                isLoading={isFetchSharePlaylistLoading}
                message={
                  <>
                    <Typography variant="subtitle1" component="p">
                      {t(
                        'PlaylistSharing.EmptyState.Title',
                        `This playlist hasn't been shared yet.`
                      )}
                    </Typography>
                    <Typography variant="subtitle1" component="p">
                      {t(
                        'PlaylistSharing.EmptyState.Description',
                        'Enter an email address above and click "Add" to share this playlist with someone.'
                      )}
                    </Typography>
                  </>
                }
              />
            </Box>
          )}
      <ConfirmationModal {...roleRemovalConfirmationProps} />
    </Box>
  )
}

export default SharedUsersList
